import { useQueryClient } from "react-query";
import { t } from "@/i18n-js/instance";
import type { ConnectionInitiator } from "@/react/api/admin/connections";
import { useShouldShowMessageButton } from "@/react/components/Profile/useShouldShowMessageButton";
import { useSendMessageToUser } from "@/react/components/shared/MembersList/hooks/useSendMessageToUser";
import { invalidateNotificationsQueryCache } from "@/react/components/shared/uikit/HeaderV3/RightActionBlock/Notifications/useHeaderNotificationsApi";
import { useToast } from "@/react/components/shared/uikit/ToastV2";
import { useAcceptConnection } from "@/react/hooks/connections/useAcceptConnection";
import { invalidateAllMemberQueries } from "./util";

export const useAcceptConnectionForUser = ({
  memberPublicUid,
  memberName,
  onSuccess,
  user,
}: {
  memberPublicUid?: string;
  memberName?: string;
  onSuccess?: () => void;
  user?: ConnectionInitiator;
}) => {
  const toast = useToast();
  const queryClient = useQueryClient();

  const shouldShowMessageCTA = useShouldShowMessageButton({
    member: user,
  });

  const { handleMessageMember, isLoading: isMessaging } =
    useSendMessageToUser();

  const { mutate: acceptConnection, isLoading: isAccepting } =
    useAcceptConnection({
      onSuccess: async () => {
        await invalidateAllMemberQueries(
          queryClient,
          memberPublicUid ?? user?.public_uid ?? "",
        );
        invalidateNotificationsQueryCache({ queryClient });

        toast.success(
          <div className="flex items-center gap-2">
            <span>
              {t("profile_drawer.connect_banner.messages.connection_accepted", {
                memberName: user?.name ?? memberName ?? "",
              })}
            </span>
            {shouldShowMessageCTA && user?.id && (
              <button
                type="button"
                className="hover:text-light underline transition-colors"
                onClick={async () => {
                  await handleMessageMember(user.id.toString());
                }}
              >
                {t("message")}
              </button>
            )}
          </div>,
          {
            duration: "long",
          },
        );
        onSuccess?.();
      },
      onError: () => {
        toast.error(
          t("profile_drawer.connect_banner.messages.connection_accept_error"),
        );
      },
    });

  return {
    acceptConnectionForUser: acceptConnection,
    isAccepting: isAccepting || isMessaging,
  };
};
